import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

type ToasterTheme = NonNullable<ToasterProps["theme"]>;

/** The themes the toaster can render. */
const TOASTER_THEMES = ["light", "dark", "system"] as const satisfies readonly ToasterTheme[];

/**
 * next-themes stores whatever string it was last given — including one written by an
 * older build, or by hand in localStorage — so the toaster is handed a theme it knows
 * or the system default.
 */
function toasterTheme(theme: string): ToasterTheme {
  return TOASTER_THEMES.find((candidate) => candidate === theme) ?? "system";
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={toasterTheme(theme)}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

// The upstream scaffold also re-exported sonner's `toast` from here. Nothing imported
// it — the app raises toasts through `~/hooks/use-toast` — and passing a non-component
// through a component module costs the module its Fast Refresh boundary. Anything that
// does want it should take it from `sonner` directly.
export { Toaster };
